import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { AlertModule } from 'ngx-bootstrap/alert';
import { CommonFormValidationMessagesComponent } from '../../forms';
import {
  LoaderGlobeAnimationDetailComponent,
  LoadingObject,
  LoadingService,
} from '../../loaders';
import { UserLinksService } from '../user-links.service';
import { UserLinkViewModel, SocialLinkType } from '../user-links.types';

@Component({
  selector: 'multisite-profile-user-social-links',
  templateUrl: './profile-user-social-links.component.html',
  styleUrls: ['./profile-user-social-links.component.scss'],
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    AlertModule,
    LoaderGlobeAnimationDetailComponent,
    CommonFormValidationMessagesComponent,
  ],
})
export class ProfileUserSocialLinksComponent {
  private readonly userLinksService = inject(UserLinksService);
  private readonly loadingService = inject(LoadingService);
  private readonly destroyRef = inject(DestroyRef);

  readonly socialLinks = signal<UserLinkViewModel[]>([]);
  readonly socialChannelTemplates = this.userLinksService.socialChannelTemplates;
  readonly submitAttempted = signal(false);
  readonly error = signal<string | null>(null);
  readonly editingLinkId = signal<number | null>(null);

  loadingObject = signal<LoadingObject>({});

  readonly newLinkForm = new FormGroup({
    type: new FormControl<SocialLinkType | ''>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    url: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(5), Validators.maxLength(2000)],
    }),
    label: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(150)], // 150 is arbitraty. The backend will allow up to 255, but we want to encourage users to keep it short.
    }),
    description: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(500)],
    }),
  });

  readonly editLinkForm = new FormGroup({
    url: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(5), Validators.maxLength(2000)],
    }),
    label: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(255)],
    }),
    description: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(500)],
    }),
  });

  readonly initialLoading = computed(() => this.socialLinks() === undefined);
  readonly isAnythingLoading = computed<boolean>(() => {
    const requestInFlight = this.userLinksService.loadingObjectSignal()['userLinks'] === true;
    const createInFlight = this.userLinksService.loadingObjectSignal()['createLink'] === true;
    const updateInFlight = this.userLinksService.loadingObjectSignal()['updateLink'] === true;
    const deleteInFlight = this.userLinksService.loadingObjectSignal()['deleteLink'] === true;
    return (
      requestInFlight ||
      createInFlight ||
      updateInFlight ||
      deleteInFlight ||
      this.loadingService.isAnythingLoading(this.loadingObject())
    );
  });

  readonly formDisabled = computed(() => this.isAnythingLoading());

  readonly availableChannels = computed(() => {
    const currentSocialLinks = this.socialLinks();
    const usedTypes = new Set(currentSocialLinks.map((link) => link.type));
    return this.socialChannelTemplates.filter((template) => !usedTypes.has(template.type));
  });

  readonly getChannelLabel = (type: SocialLinkType): string => {
    return this.socialChannelTemplates.find((t) => t.type === type)?.label || type;
  };

  readonly getChannelLabelForLink = (link: UserLinkViewModel): string => {
    const matched = this.socialChannelTemplates.find((template) => template.type === link.type);
    return matched?.label || link.type;
  };

  readonly getSelectedChannelLabel = (): string => {
    const typeValue = this.newLinkForm.get('type')?.value;
    if (!typeValue) {
      return '';
    }
    return this.getChannelLabel(typeValue as SocialLinkType);
  };

  constructor() {
    effect(() => {
      if (this.formDisabled()) {
        this.newLinkForm.disable({ emitEvent: false });
        this.editLinkForm.disable({ emitEvent: false });
      } else {
        this.newLinkForm.enable({ emitEvent: false });
        this.editLinkForm.enable({ emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.userLinksService.socialLinks$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((links) => {
        this.socialLinks.set(links);
      });

    this.userLinksService.userLinks$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((allLinks) => {
        if (allLinks.length === 0) {
          this.requestInitialLinksIfNeeded();
        }
      });

    this.requestInitialLinksIfNeeded();
  }

  private requestInitialLinksIfNeeded(): void {
    const isLoading = this.userLinksService.loadingObjectSignal()['userLinks'] === true;
    const hasLinks = this.userLinksService.userLinksState.length > 0;
    if (!isLoading && !hasLinks) {
      this.userLinksService
        .fetchUserLinks()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: (errorResponse) => {
            this.error.set(
              typeof errorResponse === 'string'
                ? errorResponse
                : errorResponse?.message ?? 'multisite.something_went_wrong',
            );
          },
        });
    }
  }

  onSelectChannel(type: SocialLinkType): void {
    this.newLinkForm.patchValue({ type });
  }

  onAddLink(): void {
    this.submitAttempted.set(true);
    if (!this.newLinkForm.valid) {
      return;
    }

    const formValue = this.newLinkForm.getRawValue();
    if (formValue.type === '') {
      return;
    }

    this.userLinksService
      .createUserLink({
        category: 'social',
        type: formValue.type as SocialLinkType,
        url: formValue.url,
        label: formValue.label || null,
        description: formValue.description || null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.newLinkForm.reset();
          this.submitAttempted.set(false);
          this.error.set(null);
        },
        error: (errorMessage) => {
          this.error.set(errorMessage);
        },
      });
  }

  onEditLink(link: UserLinkViewModel): void {
    this.editingLinkId.set(link.id);
    this.editLinkForm.patchValue({
      url: link.url,
      label: link.label || '',
      description: link.description || '',
    });
  }

  onCancelEdit(): void {
    this.editingLinkId.set(null);
    this.editLinkForm.reset();
    this.submitAttempted.set(false);
  }

  onSaveEdit(): void {
    this.submitAttempted.set(true);
    const editingId = this.editingLinkId();
    if (!editingId || !this.editLinkForm.valid) {
      return;
    }

    const formValue = this.editLinkForm.getRawValue();
    const link = this.socialLinks().find((l) => l.id === editingId);
    if (!link) {
      return;
    }

    this.userLinksService
      .updateUserLink(editingId, {
        category: link.category,
        type: link.type as SocialLinkType,
        url: formValue.url,
        label: formValue.label || null,
        description: formValue.description || null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.editingLinkId.set(null);
          this.editLinkForm.reset();
          this.submitAttempted.set(false);
          this.error.set(null);
        },
        error: (errorMessage) => {
          this.error.set(errorMessage);
        },
      });
  }

  onDeleteLink(id: number): void {
    if (!confirm('Are you sure you want to delete this link?')) {
      return;
    }

    this.userLinksService
      .deleteUserLink(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.error.set(null);
        },
        error: (errorMessage) => {
          this.error.set(errorMessage);
        },
      });
  }

  readonly isLoading = computed(() => this.initialLoading() || this.isAnythingLoading());
}
